import "./AddUnitForm.css";
import { useMemo, useCallback, useEffect, useState } from "react";
import { debounce } from 'lodash';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from 'react-hook-form';
import SelectField from "../form-fields/SelectField";
import Radio from "../form-fields/radio";
import apiHelper from "../../utils/apiHelper";
import { PropertyService, UnitService, DocumentService, DocumentEntityTypesEnum, DocumentTypesEnum, UnitVM, UnitTypesEnum } from "@propertelligent/client-api";
import CounterField from "../form-fields/CounterField";
import Uploader from "../CustomUploader/Uploader";
import InputField from "../form-fields/InputField";
import { UserLoginData } from "../../components/common/charts/Enums";
import CustomRadio from "../form-fields/CustomRadio";
import { v4 as uuidv4 } from 'uuid';
import toastUnitIcon from "../../assets/toastUnitIcon.svg"
import { GetImages } from "../../utils/GetImages";

interface Option {
  Id: string | number;
  Name: string;
}

interface UnitFormValues {
  propertyId: number | null;
  unitNumber: string;
  size: number | '';
  unitTypeId: number | null;
  numberOfBathrooms: number;
  numberOfBedrooms: number;
  isBathAttached?: string | boolean;
  mainImage: File | null | any;

}

interface UnitCreateProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  unitData?: UnitVM;
  fetchUnitDetails?: () => void;
  fetchData?: (page: number) => void;
  propertyId?: number;
  isProperty?: boolean; // New prop to indicate if entity is fixed
  formId?: string;
  onUnitAdded?: () => void;
}

const UnitCreate: React.FC<UnitCreateProps> = ({
  isModalOpen,
  setModalOpen,
  unitData,
  fetchUnitDetails,
  fetchData,
  propertyId,
  isProperty = false,
  onUnitAdded,

}) => {

  const [properties, setProperties] = useState<Option[]>([]);
  const [unitTypes, setUnitTypes] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);

  const memoizedProperties = useMemo(() => properties, [properties]);
  const memoizedUnitTypes = useMemo(() => unitTypes, [unitTypes]);
  const [mainImage, setMainImage] = useState<string | null>(null);

  const organization_id = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');

  const formId = `UnitForm`;

  // Define consistent defaultValues
  const defaultValues: UnitFormValues = {
    propertyId: propertyId || unitData?.propertyId || null,
    unitNumber: unitData?.unitNumber || '',
    size: unitData?.size || '',
    unitTypeId: unitData?.unitTypeId || null,
    numberOfBathrooms: unitData?.numberOfBathrooms || 0,
    numberOfBedrooms: unitData?.numberOfBedrooms || 0,
    isBathAttached: unitData?.isBathAttached !== undefined ? String(unitData.isBathAttached) : 'true',
    mainImage: unitData?.mainImageBase64 || null,
  };

  useEffect(() => {

    if (unitData?.mainImageBase64) {
      setMainImage("data:image/*;base64," + unitData.mainImageBase64);
    } else {
      setMainImage(null);
    }
  }, [unitData]);


  const methods = useForm<UnitFormValues>({
    defaultValues,
  });

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [propertyData, unitTypesData] = await Promise.all([
        // Only fetch properties if isProperty is false
        isProperty ? Promise.resolve([]) : fetchPropertyData(),
        fetchUnitTypesData(),
      ]);
      setProperties(propertyData);
      setUnitTypes(unitTypesData);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isProperty]);

  const fetchPropertyData = useCallback(async () => {
    const response = await apiHelper(
      PropertyService.getApiPropertyOrganizationlookup,
      { showNotification: false },
      organization_id
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.name || val.Name || val.description || '',
    })) || [];
  }, [organization_id]);

  const fetchUnitTypesData = useCallback(async () => {
    const response = await apiHelper(
      UnitService.getApiUnitUnitTypes,
      { showNotification: false }
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || '',
    })) || [];
  }, []);

  useEffect(() => {

    if (isModalOpen) {
      // Reset form with updated default values
      methods.reset({
        ...defaultValues,
        propertyId: isProperty && propertyId ? propertyId : (unitData?.propertyId || null),
      });
      fetchInitialData();
    }
  }, [isModalOpen, fetchInitialData, methods, unitData, propertyId, isProperty]);

  const unitTypeId = methods.watch("unitTypeId");
  const { control, formState: { isDirty }, setValue, getValues } = methods;

  useEffect(() => {
    if (unitTypeId == UnitTypesEnum.CondoApartment) {

      if (!getValues("numberOfBathrooms")) {
        setValue("numberOfBathrooms", 0);
      }

      if (!getValues("numberOfBedrooms")) {
        setValue("numberOfBedrooms", 0);
      }
    }
  }, [unitTypeId, setValue, getValues]);

  const oncloseModal = () => {
    setResetCounter((prev) => prev + 1);
    setModalOpen(false);
  }


  const onSubmit = async (data: UnitFormValues) => {

    try {
      setIsLoading(true);
      const unitPayload = {
        ...data,
        propertyId: Number(data.propertyId || propertyId), // Use provided propertyId if isProperty is true
        unitTypeId: Number(data.unitTypeId),
        size: Number(data.size),
        numberOfBathrooms: data.numberOfBathrooms ? Number(data.numberOfBathrooms) : undefined,
        numberOfBedrooms: data.numberOfBedrooms ? Number(data.numberOfBedrooms) : undefined,
        isBathAttached: typeof data.isBathAttached === 'string' ? data.isBathAttached === 'true' : !!data.isBathAttached,
      };

      let unitResponse;
      if (unitData?.id) {
        unitResponse = await apiHelper(
          UnitService.putApiUnit,
          {
            title: "Unit Updated",
            successMessage: "Unit updated successfully",
            showNotification: true,
            successIcon: <img src={GetImages.toastUnitIcon} alt="success" width="20" height="20" />
          },
          { ...unitPayload, id: unitData.id }
        );
      } else {
        unitResponse = await apiHelper(
          UnitService.postApiUnit,
          {
            title: "Unit Added",
            successMessage: (res) => {
              const selectedProperty = properties.find(
                (p) => p.Id === Number(data.propertyId || propertyId)
              );

              return `<strong>${res?.success?.unitNumber}</strong> added successfully`;
            },
            showNotification: true,
            successIcon: <img src={GetImages.toastUnitIcon} alt="success" width="20" height="20" />
          },
          unitPayload
        );
      }

      const unitId = unitResponse?.data?.success?.id;

      if (!unitResponse?.data) {
        return;
      }

      if (data.mainImage && unitId) {
        const imgData = {
          EntityTypeId: DocumentEntityTypesEnum.Unit,
          EntityId: unitId,
          DocumentTypeId: DocumentTypesEnum.BeforeImage,
          IsMain: true,
          FileName: data.mainImage.name,
          Document: data.mainImage,
        };

        await apiHelper(
          DocumentService.postApiDocument,
          { showNotification: false },
          imgData
        );
      }

      methods.reset(defaultValues);
      setResetCounter((prev) => prev + 1);
      setModalOpen(false);

      if (onUnitAdded) {
        onUnitAdded();
      }
      if (fetchData) fetchData(1);
      if (fetchUnitDetails) fetchUnitDetails();
    } catch (error) {
      console.error("Error submitting unit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={oncloseModal}
      title={unitData ? "Edit Unit" : "Add Unit"}
      buttonlabel={unitData ? "Save Changes" : "Add Unit"}
      formId={formId}
      maxWidth={550}
      isDirty={isDirty}
      isSubmitDisabled={isLoading}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">

                <Uploader
                  name="mainImage"
                  required={false}
                  requiredMessage="Image is required"
                  allowedFormats={["image/jpeg", "image/png", "image/jpg"]}
                  defaultImage={mainImage}
                  maxSizeMB={1}
                  resetTrigger={resetCounter}   // 👈 yeh line add karo
                />

              </div>
              <hr></hr>

              {/* Conditionally render the property dropdown */}
              {!isProperty && !propertyId && (
                <div className="col-md-12">
                  <SelectField
                    name="propertyId"
                    label="Select Property"
                    required
                    requiredMessage="Property is required"
                    options={memoizedProperties}
                    placeholderText="Select Property"
                    disabled={isLoading}
                    isSearchable={true}
                  />
                </div>
              )}

              <div className="col-md-6">
                <SelectField
                  name="unitTypeId"
                  label="Unit Type"
                  required
                  requiredMessage="Unit type is required"
                  options={memoizedUnitTypes}
                  placeholderText="Select Type"
                  disabled={isLoading}

                />
              </div>

              <div className="col-md-6">
                <InputField
                  name="unitNumber"
                  label="Unit Number"
                  required
                  requiredMessage="Unit number is required"
                  type="text"
                  placeholder="Enter Unit Number"
                  validation={{
                    minLength: {
                      value: 1,
                      message: "Unit number is required",
                    },
                  }}
                />
              </div>

              {unitTypeId == UnitTypesEnum.CondoApartment ? (
                <>
                  <div className="col-md-6">
                    <CounterField
                      name="numberOfBathrooms"
                      label="Bathroom(s)"
                      required
                      requiredMessage="Number of bathrooms is required"
                      min={0}
                      max={10}
                    />
                  </div>
                  <div className="col-md-6">
                    <CounterField
                      name="numberOfBedrooms"
                      label="Bedroom(s)"
                      required
                      requiredMessage="Number of bedrooms is required"
                      min={0}
                      max={10}
                    />
                  </div>
                </>
              ) : unitTypeId == UnitTypesEnum.Room ? (
                <div className="col-md-12">
                  <div className="d-flex flex-column form-group">
                    <CustomRadio
                      name="isBathAttached"
                      label="Bathroom Type"
                      options={[
                        { value: "true", label: "Attached" },
                        { value: "false", label: "Common" },
                      ]}
                      control={control}
                      required
                    />
                  </div>
                </div>
              ) : unitTypeId == UnitTypesEnum.Studio ? null : (
                ""
              )}

              <div className="col-md-6">
                <InputField
                  name="size"
                  label="Area Sqft"
                  required
                  requiredMessage="Size is required"
                  type="number"
                  placeholder="Enter Sqft"
                  validation={{
                    min: {
                      value: 1,
                      message: "Size must be at least 1",
                    },
                    max: {
                      value: 100000,
                      message: "Size must be less than 100,000",
                    },
                  }}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default UnitCreate;